<script lang="ts">
  import type { Job } from "@puntocrypto/core";
  import { deleteJob } from "../jobs";
  import { Circle } from "svelte-loading-spinners";
  import {
    ArrowNarrowRight,
    ChevronDown,
    ChevronUp,
    DocumentRemove,
    ExclamationCircle,
    TrendingDown,
    TrendingUp,
  } from "svelte-hero-icons";
  import { runJobImmediatelly } from "../jobs";
  import Button from "./Button.svelte";
  import { onDestroy } from "svelte";
  import JobStatus from "./JobStatus.svelte";
  import { budgetsAndAccounts } from "../accounts";

  export let job: Job;
  export let jobId: string;
  let accountName: string | undefined;

  let showDetails: boolean = false;
  let state: "idle" | "runNowPending" | "runNowCompleted" = "idle";
  let timeoutPtr: number;

  function getDisplayPrice(value: number): string {
    const currency = job.exchangeTo;
    const intl = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    });

    return intl.format(value / 1000);
  }

  function runNow() {
    if (jobId) {
      state = "runNowPending";
      runJobImmediatelly(jobId).then(() => {
        state = "idle";
      });
    }
  }

  // whenever the job and its accountId changes, reload the account into account var
  $: if (job) {
    const b = $budgetsAndAccounts;
    if (b.type === "LOADED") {
      accountName = b.db.accountsById.get(job.account.accountId)?.name;
    }
  }

  onDestroy(() => {
    if (timeoutPtr) {
      clearTimeout(timeoutPtr);
    }
  });
</script>

<li>
  <div class="flex flex-row items-center p-4 flex-wrap sm:flex-nowrap">
    <div class="flex flex-row items-center space-x flex-1">
      <span
        class="text-xs font-semibold flex overflow-hidden rounded-full text-green-600 bg-green-200 uppercase">
        <span
          class="py-2 pr-2 pl-4 bg-green-400 text-green-800  hidden sm:inline">From:</span><span
          class="pl-2 pr-4 py-2  md:pl-4">{job.source.type}</span>
      </span>

      <ArrowNarrowRight class="h-6 w-6 text-gray-200" />
      <span
        class="text-xs font-semibold flex overflow-hidden rounded-full text-pink-600 bg-pink-200 uppercase">
        <span
          class="py-2 pr-2 pl-4 bg-pink-400 text-pink-800  hidden sm:inline">Exchange:</span><span
          class="pl-2 pr-4 py-2 md:pl-4">
          {job.exchangeTo}</span>
      </span>

      <ArrowNarrowRight class="h-6 w-6 text-gray-200" />
      <span
        class="text-xs font-semibold flex overflow-hidden rounded-full text-blue-600 bg-blue-200 uppercase">
        <span
          class="py-2 pr-2 pl-4 bg-blue-400 text-blue-800 hidden sm:inline">Account:</span><span
          class="pl-2 pr-4 py-2 md:pl-4">
          {#if accountName}
            {accountName}
          {:else}
            <Circle size="12" color="#3b83f6" unit="px" duration="1s" />
          {/if}
        </span>
      </span>
    </div>
    <div class="w-full sm:w-auto mt-4 sm:mt-0">
      <JobStatus status={job.status} />
      <Button
        disabled={state !== 'idle' || job.status.type === 'running'}
        on:click={runNow}>
        {#if state === 'idle' && job.status.type !== 'running'}
          Run now
        {:else if state === 'runNowPending'}Starting…{:else}OK{/if}
      </Button>
      <button
        class="focus:outline-none"
        on:click={() => {
          showDetails = !showDetails;
        }}>
        {#if showDetails}
          <ChevronUp class="inline w-6 h-6 ml-4" />
        {:else}
          <ChevronDown class="inline w-6 h-6 ml-4" />
        {/if}
      </button>
    </div>
  </div>
  {#if showDetails}
    <div
      class="flex flex-col space-y-2 sm:flex-row p-4 bg-gray-50 border border-t border-gray-100 items-left sm:items-center justify-between">
      <div class="max-w-full overflow-hidden">
        <span class="block capitalize text-xs font-black">ADDRESS</span>
        <span
          class="overflow-ellipsis overflow-hidden block max-w-full">{job.source.address}</span>
      </div>
      <div class="max-w-full overflow-hidden">
        <span class="block capitalize text-xs font-black">LAST RUN</span>
        <span>{job.lastRun ? `${job.lastRun.toLocaleDateString()} ${job.lastRun.toLocaleTimeString()}` : 'not yet'}</span>
      </div>
      <div class="max-w-full overflow-hidden">
        <span class="block capitalize text-xs font-black">CHANGE</span>
        {#if Math.abs(job.lastChange.milliDiff) > 0}
          {#if job.lastChange.milliDiff > 0}
            <span class="items-center inline-flex">
              <TrendingUp class="h-4 w-4 mr-2 text-green-500" />
              <span
                class="text-green-500 font-bold">{getDisplayPrice(job.lastChange.milliDiff)}</span>
            </span>
          {:else}
            <span class="items-center inline-flex">
              <TrendingDown class="h-4 w-4 mr-2 text-red-500" />
              <span
                class="text-red-500 font-bold">{getDisplayPrice(job.lastChange.milliDiff)}</span>
            </span>
          {/if}
        {:else}<span class="text-gray-400 font-bold">No Change</span>{/if}
      </div>
      {#if job.id}
        <Button
          on:click={() => job.id && deleteJob(job.id)}
          class="inline-flex">
          <DocumentRemove class="h-4 w-4 mr-2" />
          Delete Job
        </Button>
      {/if}
    </div>
  {/if}
  {#if job.status.type === 'failed'}
    <p class="items-center p-4 bg-red-100 text-right text-red-500">
      <ExclamationCircle
        class="text-red-500 w-6 h-6 inline mr-2" />{job.status.error}
    </p>
  {/if}
</li>
